import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function useFollowing() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['following', user?.id],
    queryFn: async () => {
      if (!user) return new Set<string>();
      const { data, error } = await supabase
        .from('user_follows')
        .select('following_id')
        .eq('follower_id', user.id);
      if (error) throw error;
      return new Set((data || []).map(f => f.following_id));
    },
    enabled: !!user,
  });
}

export function useFollowCounts(userIds: string[]) {
  return useQuery({
    queryKey: ['follow-counts', userIds.sort().join(',')],
    queryFn: async () => {
      if (!userIds.length) return new Map<string, number>();
      const { data, error } = await supabase
        .from('user_follows')
        .select('following_id')
        .in('following_id', userIds);
      if (error) throw error;
      const counts = new Map<string, number>();
      (data || []).forEach(f => {
        counts.set(f.following_id, (counts.get(f.following_id) || 0) + 1);
      });
      return counts;
    },
    enabled: userIds.length > 0,
  });
}

export function useToggleFollow() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, isFollowing }: { userId: string; isFollowing: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      if (userId === user.id) throw new Error("Can't follow yourself");
      if (isFollowing) {
        await supabase.from('user_follows').delete().eq('follower_id', user.id).eq('following_id', userId);
      } else {
        await supabase.from('user_follows').insert({ follower_id: user.id, following_id: userId });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['following'] });
      qc.invalidateQueries({ queryKey: ['follow-counts'] });
    },
  });
}
